import { type ImageModel } from '@backend/lib/ai/functions/generateImage'
import { Button } from '@/components/ui/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import { ChevronDownIcon } from 'lucide-react'
import { Fragment } from 'react'
import {
  FREE_TIER_IMAGE_MODELS,
  PRO_TIER_IMAGE_MODELS,
} from '@backend/lib/service/usageLimit/constants'
import { trpc } from '@/config/trpc'

// Mirrors the server-side gates so paid models read as locked before the user
// clicks. The enforced copies live in the usage-limit constants.
const FREE_MODELS = new Set<ImageModel>(FREE_TIER_IMAGE_MODELS)
const PRO_MODELS = new Set<ImageModel>(PRO_TIER_IMAGE_MODELS)

/** Which gate a locked model sits behind; matches the upgrade modal's limit types. */
export type ModelUpgradeLimit = 'model' | 'proModel'

type ModelGroup = 'flux' | 'gemini' | 'microsoft' | 'openai'

const MODEL_GROUPS: { group: ModelGroup; label: string }[] = [
  { group: 'flux', label: 'Flux' },
  { group: 'gemini', label: 'Gemini' },
  { group: 'microsoft', label: 'Microsoft MAI' },
  { group: 'openai', label: 'OpenAI GPT Image' },
]

const IMAGE_MODEL_OPTIONS: {
  value: ImageModel
  label: string
  description: string
  group: ModelGroup
  isNew?: boolean
}[] = [
  {
    value: 'flux-2-pro',
    label: 'Flux.2 Pro',
    description: 'Best quality-to-speed ratio',
    group: 'flux',
  },
  {
    value: 'flux-2-max',
    label: 'Flux.2 Max',
    description: 'Highest quality, web grounding',
    group: 'flux',
  },
  {
    value: 'flux-2-flex',
    label: 'Flux.2 Flex',
    description: 'Full control over steps & guidance',
    group: 'flux',
  },
  {
    value: 'flux-2-klein',
    label: 'Flux.2 Klein',
    description: 'Fastest, lowest cost',
    group: 'flux',
  },
  {
    value: 'flux-3-klein',
    label: 'Flux 3 Klein',
    description: 'Fast and low cost, up to 4K',
    group: 'flux',
    isNew: true,
  },
  {
    value: 'gemini-2.5-flash-image',
    label: 'Gemini Flash 2.5',
    description: 'Fast and cheap',
    group: 'gemini',
  },
  {
    value: 'gemini-3.1-flash-image-preview',
    label: 'Gemini Flash 3.1',
    description: 'Near-Pro quality, up to 4K',
    group: 'gemini',
  },
  {
    value: 'gemini-3-pro-image-preview',
    label: 'Gemini Pro 3',
    description: 'Best quality, 4K, best text rendering',
    group: 'gemini',
  },
  {
    value: 'gemini-nano-banana-2.1',
    label: 'Nano Banana 2.1',
    description: 'High quality at Flash prices',
    group: 'gemini',
    isNew: true,
  },
  {
    value: 'mai-image-2.6',
    label: 'MAI Image 2.6',
    description: 'Top-tier quality, polished layouts',
    group: 'microsoft',
    isNew: true,
  },
  {
    value: 'mai-image-2.6-flash',
    label: 'MAI Image 2.6 Flash',
    description: 'High quality, faster',
    group: 'microsoft',
    isNew: true,
  },
  {
    value: 'gpt-image-2.5-sunburst',
    label: 'GPT Image 2.5 Sunburst',
    description: 'Highest-rated quality, slower',
    group: 'openai',
    isNew: true,
  },
  {
    value: 'gpt-image-2.5-flare',
    label: 'GPT Image 2.5 Flare',
    description: 'Near-top quality',
    group: 'openai',
    isNew: true,
  },
]

/** The provider aliases resolve to the provider's default model. */
function resolveAlias(model: ImageModel): ImageModel {
  if (model === 'flux') return 'flux-2-pro'
  if (model === 'gemini') return 'gemini-2.5-flash-image'
  return model
}

function getDisplayLabel(model: ImageModel): string {
  return IMAGE_MODEL_OPTIONS.find((o) => o.value === resolveAlias(model))?.label ?? model
}

interface Props {
  value: ImageModel
  onChange: (model: ImageModel) => void
  disabled?: boolean
  isFreePlan?: boolean
  onUpgradeClick?: (limit: ModelUpgradeLimit) => void
}

export function ImageModelPicker({ value, onChange, disabled, isFreePlan, onUpgradeClick }: Props) {
  const { data: subscription } = trpc.userSubscription.get.useQuery()
  const hasProModels = subscription?.hasProImageModels ?? false

  const lockedBehind = (model: ImageModel): ModelUpgradeLimit | null => {
    if (PRO_MODELS.has(model)) return hasProModels ? null : 'proModel'
    return isFreePlan && !FREE_MODELS.has(model) ? 'model' : null
  }

  const handleSelect = (model: ImageModel) => {
    const limit = lockedBehind(model)
    if (limit) {
      onUpgradeClick?.(limit)
      return
    }
    onChange(model)
  }

  const renderItem = (option: (typeof IMAGE_MODEL_OPTIONS)[number], isSelected: boolean) => {
    const limit = lockedBehind(option.value)

    return (
      <DropdownMenuItem
        key={option.value}
        onClick={() => handleSelect(option.value)}
        className={isSelected ? 'bg-hover' : ''}>
        <div className="flex w-full items-start justify-between gap-2">
          <div className="flex flex-col">
            <span className="flex items-center gap-1.5">
              {option.label}
              {option.isNew && (
                <span className="border-primary text-primary rounded border px-1 text-[9px] font-semibold leading-tight">
                  New
                </span>
              )}
            </span>
            <span className="text-muted-foreground text-[11px]">{option.description}</span>
          </div>
          {limit && (
            <span className="bg-primary mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold text-white">
              {limit === 'proModel' ? 'Pro' : 'Standard'}
            </span>
          )}
        </div>
      </DropdownMenuItem>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" disabled={disabled} className="gap-1">
          {getDisplayLabel(value)}
          <ChevronDownIcon className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {MODEL_GROUPS.map(({ group, label }, index) => (
          <Fragment key={group}>
            {index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel>{label}</DropdownMenuLabel>
            {IMAGE_MODEL_OPTIONS.filter((option) => option.group === group).map((option) =>
              renderItem(option, option.value === resolveAlias(value)),
            )}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
